/**
 * Admin model-override hooks (Batch 21a-3 §4).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { queryClient } from "@/lib/queryClient";

export type ModelOverrideActionType =
  | "ranking"
  | "interview_prep"
  | "ai_evaluation"
  | "interview_analysis"
  // Phase 2.7 (2026-05-15): resume_format added to the override surface.
  // Mirrors backend Pydantic Literal in
  // app/schemas/admin/model_overrides.py::CreateOverrideBody.action_type
  // and the DB CHECK ck_tenant_model_overrides_action_type (migration 0061).
  | "resume_format";

interface ModelOverrideItem {
  action_type: string;
  model_name: string;
  reason: string;
  set_by_super_admin_id: string;
  set_at: string;
  updated_at: string;
}

interface ListResponse {
  overrides: ModelOverrideItem[];
}

interface SingleResponse {
  override: ModelOverrideItem;
}

interface CreateOverrideBody {
  action_type: ModelOverrideActionType;
  model_name: string;
  reason: string;
}

export function useAdminModelOverrides(tenantId: string | undefined) {
  return useQuery<ListResponse, ApiError>({
    queryKey: ["admin", "tenants", tenantId, "modelOverrides"],
    queryFn: async () => {
      const r = await apiClient.get<ListResponse>(
        `/admin/tenants/${tenantId}/model-overrides`
      );
      return r.data;
    },
    enabled: !!tenantId,
  });
}

export function useUpsertModelOverride(tenantId: string | undefined) {
  return useMutation<SingleResponse, ApiError, CreateOverrideBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<SingleResponse>(
        `/admin/tenants/${tenantId}/model-overrides`,
        body
      );
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId, "modelOverrides"],
      });
    },
  });
}

export function useDeleteModelOverride(tenantId: string | undefined) {
  return useMutation<{ action_type: string; prior_model: string }, ApiError, string>({
    mutationFn: async (actionType) => {
      const r = await apiClient.delete<{ action_type: string; prior_model: string }>(
        `/admin/tenants/${tenantId}/model-overrides/${actionType}`
      );
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId, "modelOverrides"],
      });
    },
  });
}
